import React, { useReducer, useContext, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { usePostCodeAddress } from '../../../Hooks/usePostCodeAddress';
import VisitorParamsContext from '../../../Contexts/VisitorParams';
import PostCodeManual from './PostCodeManual';
import GTMDataLayer from '../../../Utility/GTMDataLayer';

const InfoBanner = () => (
  <div className="d-flex align-items-center gap-4 bg-blue-50-half rounded-4 p-3 mt-4 border border-blue-100 shadow-sm animate-pop-in">
    <div className="flex-shrink-0 bg-white rounded-3 shadow-sm d-flex align-items-center justify-content-center p-2 w-12 h-12">
      <svg viewBox="0 0 100 100" className="w-100 h-100">
        <circle cx="50" cy="35" r="15" fill="#cfd8dc" />
        <rect x="30" y="55" width="40" height="30" rx="10" fill="#2196f3" />
        <circle cx="20" cy="50" r="10" fill="#4caf50" />
        <path d="M15 50 L18 53 L25 45" stroke="white" strokeWidth="3" fill="none" />
      </svg>
    </div>
    <p className="fw-semibold text-gray-600 leading-tight mb-0 fs-12">
      Your personal information is vital for verifying your identity and finance agreement,
      allowing us to assess your PCP claim accurately.
    </p>
  </div>
);

const initialState = {
  lookUp: true,
  nextClick: false,
  checkSelect: false,
  getDetails: [],
  getAddress: [],
  showManualAddress: 'hide',
  getPostcode: '#',
  isManualAddress: false,
};

const postcodeReducer = (state, action) => {
  switch (action.type) {
    case 'validationSucess':
      return {
        ...state,
        lookUp: action.payload.lookup,
        nextClick: action.payload.next_click,
        getPostcode: action.payload.postcode,
        getAddress: action.payload.get_address,
        checkSelect: action.payload.check_select,
        showManualAddress: action.payload.manual_address,
        isManualAddress: false,
        hasLookupError: false,
      };
    case 'getExtraAddressDetails':
      return {
        ...state,
        getDetails: action.payload.get_details,
        showManualAddress: action.payload.manual_address,
      };
    case 'showManualDiv':
      return {
        ...state,
        showManualAddress: 'show',
        lookUp: false,
        nextClick: true,
        checkSelect: false,
        clickManualLink: action.payload.manual_click,
      };
    case 'resetaddress':
      return {
        ...state,
        getDetails: [],
        getAddress: [],
        showManualAddress: 'hide',
        lookUp: true,
        nextClick: false,
        checkSelect: false,
      };
    case 'lookupError':
      return {
        ...state,
        hasLookupError: true,
        checkSelect: false,
        isManualAddress: false,
        getDetails: [],
        getAddress: [],
      };
    case 'enableManualAddress':
      return {
        ...state,
        isManualAddress: true,
        hasLookupError: false,
        checkSelect: false,
        getDetails: [],
        getAddress: [],
        showManualAddress: 'hide',
      };
    case 'resetAll':
      return initialState;
    default:
      return state;
  }
};

const IdentityVerification = forwardRef(({
  slideChange,
  slideView,
  previousSlideChange,
  register,
  validationMsg,
  trigger,
  clearErrors,
  getValues,
  setError,
  setValue,
  watch,
  setPcodemanual, // Receive setPcodemanual
  vendorRef, // Receive vendorRef
  setIsNextActive,
}, ref) => {
  const { getPostcodeAddress, getSplitPostcodeAddress } = usePostCodeAddress();
  const [state, dispatch] = useReducer(postcodeReducer, initialState);
  const { visitorParameters } = useContext(VisitorParamsContext);
  const scrollToEvent = useRef(null);
  const txtPostCode = watch("txtPostCode");
  const address1 = watch("address1");
  const txtHouseNumber = watch("txtHouseNumber");
  const txtTown = watch("txtTown");
  const txtCountry = watch("txtCountry");
  const txtCounty = watch("txtCounty");
  const txtDistrict = watch("txtDistrict");

  const isButtonActive = state.isManualAddress
    ? (
        txtPostCode?.trim() &&
        txtHouseNumber?.trim() &&
        txtTown?.trim() &&
        txtCountry?.trim() &&
        txtCounty?.trim() &&
        txtDistrict?.trim()
      )
    : (
        txtPostCode?.trim() &&
        address1 &&
        txtCountry
      );

  // Keep parent sticky button in sync
  useEffect(() => {
    if (slideView !== 'show') return;
    setIsNextActive(!!isButtonActive);
  }, [isButtonActive, slideView]);

  const checkValidation = async (e) => {
    e.preventDefault();
    dispatch({ type: 'resetaddress', get_address: [] });
    setPcodemanual(0); // Reset pCodemanual on new lookup
    vendorRef.current = ""; // Reset vendorRef on new lookup
    const txtPostCode = await trigger('txtPostCode');
    if (txtPostCode) {
      let hasFinished = false;
      const timeoutId = setTimeout(() => {
        if (!hasFinished) {
          hasFinished = true;
          dispatch({ type: 'enableManualAddress' });
          setPcodemanual(1); // Set pCodemanual to 1 if manual address is enabled due to timeout
        }
      }, 10000);

      await getPostcodeAddress(e.target.value, visitorParameters.visitor_parameters.uuid)
        .then((res) => {
          if (hasFinished) return;
          hasFinished = true;
          clearTimeout(timeoutId);
          if (res.data.status === '0') {
            if (res.data.Error === 'Invalid Postcode or regex not matching') {
              setError('txtPostCode', { type: 'manual', message: 'Please Enter Valid Postcode' });
              dispatch({ type: 'resetAll' });
              return;
            } else {
              clearErrors();
              setPcodemanual(1); // Set pCodemanual to 1 if manual address is enabled
              dispatch({ type: 'enableManualAddress' });
              return;
            }
          }
          if (res.data.length > 0) {
            dispatch({
              type: 'validationSucess',
              payload: {
                lookup: false,
                next_click: true,
                postcode: e.target.value,
                get_address: res.data,
                check_select: true,
                manual_address: 'hide',
              },
            });
          }
        })
        .catch(() => {
          setError('txtPostCode', { type: 'manual', message: 'Please Enter Valid Postcode' });
        });
    }
  };

  const getValue = async (e) => {
    e.preventDefault();
    clearErrors(['txtHouseNumber', 'txtAddress3', 'txtCounty', 'txtTown', 'txtCountry']);
    if (e.target.value !== '') {
      const getData = await getSplitPostcodeAddress(
        e.target.value,
        state.getPostcode,
        visitorParameters.visitor_parameters.uuid
      );
      const postcodeval = getData?.data?.postcode || '';
      if (postcodeval === '') {
        clearErrors();
        setPcodemanual(1); // Set pCodemanual to 1 if manual address is enabled
        dispatch({ type: 'enableManualAddress' });
        return;
      }
      dispatch({
        type: 'getExtraAddressDetails',
        payload: { get_details: getData.data, manual_address: 'show' },
      });
      clearErrors(['address1', 'txtHouseNumber', 'txtAddress3', 'txtCounty', 'txtTown', 'txtCountry']); // Clear errors for address fields
      const { line_1, line_2, line_3, town, county, country, vendor, district } = getData.data;
      setValue('txtPostCode', postcodeval);
      setValue('txtHouseNumber', line_1);
      setValue('txtAddress2', line_2);
      setValue('txtAddress3', line_3);
      setValue('txtTown', town);
      setValue('txtDistrict', district);
      setValue('txtCounty', county);
      setValue('txtCountry', country);
      vendorRef.current = vendor || ""; // Update vendorRef
      setPcodemanual(0); // Set pCodemanual to 0 for successful lookup
      setTimeout(() => {
        scrollToEvent.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50);
    } else {
      dispatch({
        type: 'getExtraAddressDetails',
        payload: { get_details: [], manual_address: 'hide' },
      });
    }
  };

  const handleNext = async (e) => {
    if (state.isManualAddress && state.hasLookupError === false) {
      const validManual = await trigger(['txtHouseNumber', 'txtTown', 'txtCountry', 'txtPostCode']);
      if (!validManual) return;
      setValue('pCode_manual', 1);
      setPcodemanual(1); // Ensure pCodemanual is 1 for manual entry
      vendorRef.current = ""; // Clear vendorRef for manual entry
      GTMDataLayer({ clickEvent: 'slideclick', question: 'postcode', answer: '' });
      slideChange({ target: { name: "next3" } });
      return;
    }
    setValue('pCode_manual', 0);
    setPcodemanual(0); // Ensure pCodemanual is 0 for lookup
    let errorFlag = 0;
    const txtPostCode = await trigger('txtPostCode');
    var txtAddress;
    var txtCountry;
    if (txtPostCode) {
      txtAddress = await trigger('address1');
      txtCountry = await trigger('txtCountry');
    } else {
      document.getElementById('txtPostCode')?.focus();
      return false;
    }
    if (!txtAddress) {
      document.getElementById('address1')?.focus();
      return false;
    }
    if (!txtCountry) {
      setError('address1', { type: 'manual', message: 'Please select valid address' });
      return false;
    }
    if (!txtPostCode || !txtAddress || !txtCountry) errorFlag = 1;
    if (errorFlag === 0) {
      GTMDataLayer({ clickEvent: 'slideclick', question: 'postcode', answer: '' });
      slideChange({ target: { name: "next3" } });
    } else {
      return false;
    }
  };

  // Expose handleNext to parent via ref
  useImperativeHandle(ref, () => ({ handleNext }));

  return (
    <div className={`slide ${slideView === 'show' ? 'active' : 'd-none'}`} id="slide-3">
      <h2 className="fs-4 fw-bold tracking-tight text-gray-900">Identity Verification</h2>
      <p className="fw-medium text-gray-700 mb-5 fs-15">
        We'll just need a few more details to run a soft credit check
      </p>

      <div className="mx-auto animate-stagger-up delay-400">
        {/* <div className="col-12 d-flex mb-1"> */}
          {/* <div className="col-6"> */}
            <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-3 transition-colors rounded-10">
              <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
                Postcode <span className="text-red-500">*</span>
              </legend>
              <input
                type="text"
                placeholder="Postcode"
                name="txtPostCode"
                id="txtPostCode"
                className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2 text-uppercase"
                style={{ outline: 'none' }}
                onBlur={(e) => { e.target.value = e.target.value.trim(); checkValidation(e); }}
                autoComplete="off"
                ref={register({
                  required: 'Please Enter Postcode',
                  pattern: { value: /^[A-Za-z0-9 ]+$/, message: 'Please Enter a Valid Postcode' },
                  minLength: { value: 5, message: 'Please Enter a Valid Postcode' },
                })}
              />
            </fieldset>
          {/* </div> */}
          {/* <div className="col-6 ms-1"> */}
            <button
              type="button"
              className="btn-lookup w-100 py-3 rounded-3 fw-bold border-0 mb-4 d-flex align-items-center justify-content-center transition-all shadow-sm"
              onClick={() => trigger('txtPostCode')}
            >
              Look Up Address
            </button>
          {/* </div> */}
        {/* </div> */}
        {validationMsg?.txtPostCode && (
          <p className="error_msg">{validationMsg.txtPostCode.message}</p>
        )}

        {state.isManualAddress && (
          <>
            <p>
              Sorry we are unable to fetch your address details, please enter your address manually
            </p>
            <PostCodeManual {...{ register, validationMsg }} />
          </>
        )}

        {state.checkSelect && !state.isManualAddress && (
          <div id="currentAddressCollapse" style={{ display: 'block' }}>
            <div className="col-lg-12 mb-1">
              <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
                <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
                  Select Address <span className="text-red-500">*</span>
                </legend>
                <div className="position-relative">
                  <select
                    name="address1"
                    className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2 cursor-pointer"
                    style={{ outline: 'none' }}
                    ref={register({ required: 'Please select address' })}
                    onChange={getValue}
                  >
                    {state.getAddress.map((addr, i) => (
                      <option key={i} value={addr.id}>{addr.address}</option>
                    ))}
                  </select>
                </div>
              </fieldset>
              {validationMsg?.address1 && (
                <p className="error_msg">{validationMsg.address1.message}</p>
              )}
            </div>

            <div className={state.showManualAddress === 'show' ? 'row' : 'd-none'}>
              {state.getDetails.line_1 && (
                <div className="mb-3">
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">Address Line 1</legend>
                    <input type="text" name="txtHouseNumber" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register({ required: true })} defaultValue={state.getDetails.line_1} />
                  </fieldset>
                </div>
              )}
              {state.getDetails.line_2 && (
                <div className="mb-3">
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">Address Line 2</legend>
                    <input type="text" name="txtAddress2" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register()} defaultValue={state.getDetails.line_2} />
                  </fieldset>
                </div>
              )}
              {state.getDetails.line_3 && (
                <div className="mb-3">
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">Address Line 3</legend>
                    <input type="text" name="txtAddress3" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register()} defaultValue={state.getDetails.line_3} />
                  </fieldset>
                </div>
              )}
              {state.getDetails.town && (
                <div className="mb-3">
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">Town</legend>
                    <input type="text" name="txtTown" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register({ required: true })} defaultValue={state.getDetails.town} />
                  </fieldset>
                </div>
              )}
              {state.getDetails.county && (
                <div className="mb-3">
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">County</legend>
                    <input type="text" name="txtCounty" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register()} defaultValue={state.getDetails.county} />
                  </fieldset>
                </div>
              )}
              {state.getDetails.country && (
                <div className="mb-3">
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">Country</legend>
                    <input type="text" name="txtCountry" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register({ required: true })} defaultValue={state.getDetails.country} />
                  </fieldset>
                </div>
              )}
              {state.getDetails.district && (
                <div className="d-none"> {/* Hidden field for District */}
                  <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 transition-colors rounded-10">
                    <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">District</legend>
                    <input type="text" name="txtDistrict" className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2" style={{ outline: 'none' }} readOnly ref={register()} defaultValue={state.getDetails.district} />
                  </fieldset>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <InfoBanner />

      {/* Spacer so sticky bar doesn't overlap content */}
      <div style={{ height: '5rem' }} ref={scrollToEvent}></div>
    </div>
  );
});

export default IdentityVerification;